import { useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Map as MapLibreMap, type StyleSpecification } from 'maplibre-gl';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import 'canvas-toBlob'; // Import canvas-toBlob polyfill like original
import useGeoData from '../../../store/useGeoData';
import type { PrintUnit, PrintOutputFormat } from '../../../store/printMapsSlice';

interface PrintMapParams {
  width: number;
  height: number;
  dpi: number;
  unit: PrintUnit;
  format: PrintOutputFormat;
  zoom: number;
  center: [number, number];
  bearing: number;
  style: string | StyleSpecification;
  pitch: number;
}

export const usePrintMaps = () => {
  const { 
    printWidth, 
    printHeight, 
    printDpi, 
    printUnit, 
    printOutputFormat, 
    isGenerating,
    setIsGenerating 
  } = useGeoData(useShallow((state) => ({
    printWidth: state.printWidth,
    printHeight: state.printHeight,
    printDpi: state.printDpi,
    printUnit: state.printUnit,
    printOutputFormat: state.printOutputFormat,
    isGenerating: state.isGenerating,
    setIsGenerating: state.setIsGenerating,
  })));

  const toPixels = useCallback((length: number, unit: PrintUnit): string => {
    // Replicate original conversion exactly
    let conversionFactor = 96;
    if (unit === 'mm') {
      conversionFactor /= 25.4;
    }
    return conversionFactor * length + 'px';
  }, []);

  const toPixelsNumber = useCallback((length: number, unit: PrintUnit): number => {
    let conversionFactor = 96;
    if (unit === 'mm') {
      conversionFactor /= 25.4;
    }
    return conversionFactor * length;
  }, []);

  const createPrintMap = useCallback(async (params: PrintMapParams) => {
    const { width, height, dpi, unit, format, zoom, center, bearing, style, pitch } = params;

    setIsGenerating(true);
    
    // Small delay to allow the overlay to render before starting the heavy operations
    await new Promise(resolve => setTimeout(resolve, 100));
    
    try {
      // Calculate pixel ratio
      const actualPixelRatio = window.devicePixelRatio;
      Object.defineProperty(window, 'devicePixelRatio', {
        get: function() {return dpi / 96}
      });

      // Create map container
      const hidden = document.createElement('div');
      hidden.className = 'hidden-map';
      hidden.style.position = 'fixed';
      hidden.style.top = '-10000px';
      hidden.style.left = '-10000px';
      hidden.style.visibility = 'hidden';
      hidden.style.pointerEvents = 'none';
      document.body.appendChild(hidden);
      const container = document.createElement('div');
      
      // Keep CSS dimensions the same - let devicePixelRatio handle the density
      container.style.width = toPixels(width, unit);
      container.style.height = toPixels(height, unit);
      
      const expectedWidthPx = Math.round(toPixelsNumber(width, unit) * (dpi / 96));
      const expectedHeightPx = Math.round(toPixelsNumber(height, unit) * (dpi / 96));
      
      console.log(`Creating container: ${toPixels(width, unit)} x ${toPixels(height, unit)} at ${dpi} DPI`);
      console.log(`Expected final resolution: ${expectedWidthPx} x ${expectedHeightPx} pixels`);
      
      hidden.appendChild(container);
      

      // Create print map (replicate original exactly)
      const renderMap = new MapLibreMap({
        container: container,
        center: center,
        zoom: zoom,
        style: style,
        bearing: bearing,
        pitch: pitch,
        interactive: false,
        preserveDrawingBuffer: true,
        fadeDuration: 0,
        attributionControl: false,
        maxCanvasSize: [16384, 16384] // Override default 4096x4096 limit
      } as any);

      // Use original's simple approach - no async/await
      renderMap.once('idle', function() {
        const mapCanvas = renderMap.getCanvas();
        console.log(`Actual canvas size: ${mapCanvas.width} x ${mapCanvas.height} pixels`);
        console.log(`Canvas CSS size: ${mapCanvas.style.width} x ${mapCanvas.style.height}`);
        console.log(`Actual devicePixelRatio when MapLibre rendered: ${window.devicePixelRatio}`);
        console.log(`Total pixels: ${mapCanvas.width * mapCanvas.height} (${(mapCanvas.width * mapCanvas.height / 1000000).toFixed(1)}M)`);
        
        // Check WebGL context limits like original
        const gl = mapCanvas.getContext('webgl') || mapCanvas.getContext('experimental-webgl') as WebGLRenderingContext;
        if (gl) {
          const maxSize = gl.getParameter(gl.MAX_RENDERBUFFER_SIZE);
          console.log(`WebGL MAX_RENDERBUFFER_SIZE: ${maxSize}`);
        }
        
        if (format === 'png') {
          mapCanvas.toBlob(function(blob) {
            if (blob) {
              saveAs(blob, 'map.png');
            }
          });
        } else {
          const pdf = new jsPDF({
            orientation: width > height ? 'l' : 'p',
            unit: unit,
            format: [width, height],
            compress: true
          });

          pdf.addImage(mapCanvas.toDataURL('image/png'),
            'PNG', 0, 0, width, height, undefined, 'FAST');

          const currentState = useGeoData.getState();
          const mapState = currentState.mapState;
          
          pdf.setProperties({
            title: 'Map Export',
            subject: `center: [${mapState.longitude.toFixed(4)}, ${mapState.latitude.toFixed(4)}, ${mapState.zoom.toFixed(2)}]`,
            creator: 'Map Editor',
            author: 'Generated with Map Editor'
          });

          pdf.save('map.pdf');
        }

        // Cleanup (moved inside idle callback)
        renderMap.remove();
        document.body.removeChild(hidden);
        
        Object.defineProperty(window, 'devicePixelRatio', {
          get: function() {return actualPixelRatio}
        });
        
        setIsGenerating(false);
      });

    } catch (error) {
      console.error('Error generating print map:', error);
      setIsGenerating(false);
      throw error;
    }
  }, [toPixels, toPixelsNumber, setIsGenerating]);

  const generatePrintMap = useCallback(async () => {
    const currentState = useGeoData.getState();
    const map = currentState.mapInstance;
    
    if (!map) {
      throw new Error('No map instance available');
    }

    // Get map state BEFORE calling createPrintMap (like original)
    const zoom = map.getZoom();
    const center = map.getCenter();
    const bearing = map.getBearing();
    const pitch = map.getPitch();
    const style = map.getStyle();

    await createPrintMap({
      width: printWidth,
      height: printHeight,
      dpi: printDpi,
      unit: printUnit,
      format: printOutputFormat,
      zoom,
      center: [center.lng, center.lat],
      bearing,
      style,
      pitch
    });
  }, [createPrintMap, printWidth, printHeight, printDpi, printUnit, printOutputFormat]);

  return {
    generatePrintMap,
    isGenerating,
    toPixels,
    toPixelsNumber
  };
};